import React, { useState } from "react";
import clsx from "clsx";

const SimpleCheckbox = ({
  name,
  initialChecked,
  onChange,
  label,
  allowInteraction = true,
}: {
  name: string;
  initialChecked: boolean;
  onChange: (newChecked: boolean) => void;
  label?: string | React.ReactNode;
  allowInteraction?: boolean;
}) => {
  const [checkState, setCheckedState] = useState(initialChecked ?? false);

  let isLabelReactNode = React.isValidElement(label);

  return (
    <div className={clsx("flex", isLabelReactNode && "items-center", !isLabelReactNode && "gap-3")}>
      <div className="flex h-6 shrink-0 items-center">
        <div className="group grid size-4 grid-cols-1">
          <input
            name={name}
            id={name}
            type="checkbox"
            className={clsx(
              "col-start-1 row-start-1 appearance-none rounded",
              "bg-white border-[1.5px] border-primary-600",
              "checked:border-primary-600 checked:bg-primary-600",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600",
              "indeterminate:border-primary-600 indeterminate:bg-primary-600",
              "disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100",
              "forced-colors:appearance-auto",
            )}
            checked={checkState}
            onClick={(e) => {
              if (!allowInteraction) {
                return;
              }

              e.stopPropagation();
              if (onChange) {
                onChange(!checkState);
              }
              setCheckedState((s) => !s);
            }}
          />
          <svg
            fill="none"
            viewBox="0 0 14 14"
            className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
          >
            <path
              d="M3 8L6 11L11 3.5"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="opacity-0 group-has-[:checked]:opacity-100"
            />
            <path
              d="M3 7H11"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="opacity-0 group-has-[:indeterminate]:opacity-100"
            />
          </svg>
        </div>
      </div>

      {label &&
        (isLabelReactNode ? (
          label
        ) : (
          <div className="text-sm/6">
            <label htmlFor={name} className="font-medium text-[14px] text-neutral-900">
              {label}
            </label>
          </div>
        ))}
    </div>
  );
};

// TODO(saurabh): refactor to reduce duplication with SimpleCheckbox
function SimpleCheckboxControlled({
  name,
  checked,
  onChange,
  label,
  disabled,
}: {
  name: string;
  checked: boolean;
  onChange: (newChecked: boolean) => void;
  label?: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex gap-3">
      <div className="flex h-6 shrink-0 items-center">
        <div className="group grid size-4 grid-cols-1">
          <input
            name={name}
            id={name}
            type="checkbox"
            className={clsx(
              "col-start-1 row-start-1 appearance-none rounded",
              "bg-white border-[1.5px] border-primary-600",
              "checked:border-primary-600 checked:bg-primary-600",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600",
              "indeterminate:border-primary-600 indeterminate:bg-primary-600",
              "disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100",
              "forced-colors:appearance-auto",
            )}
            checked={checked}
            onClick={(e: React.MouseEvent<HTMLInputElement>) => {
              if (disabled) {
                return;
              }
              e.stopPropagation();
              if (onChange) {
                onChange(e.currentTarget.checked);
              }
            }}
          />
          <svg
            fill="none"
            viewBox="0 0 14 14"
            className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
          >
            <path
              d="M3 8L6 11L11 3.5"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="opacity-0 group-has-[:checked]:opacity-100"
            />
            <path
              d="M3 7H11"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="opacity-0 group-has-[:indeterminate]:opacity-100"
            />
          </svg>
        </div>
      </div>
      {label && (
        <div className="text-sm/6">
          <label htmlFor={name} className="font-medium text-[14px] text-neutral-900">
            {label}
          </label>
        </div>
      )}
    </div>
  );
}

export { SimpleCheckbox, SimpleCheckboxControlled };
